import exifr from 'exifr';

/**
 * 写真ファイルの EXIF から GPS 位置と撮影時刻を読み取る (issue #23、写真まとめて一括投稿)。
 *
 * **加工前の原本 File に対して呼ぶこと** — サムネ生成や圧縮 (canvas 再エンコード) は EXIF を
 * 落とすため、GPS は必ず処理前に読む。読み取った GPS は投稿の location に保存し、Storage に
 * アップロードする画像は別途 EXIF 除去する (docs/13 §8-2 プライバシー方針)。
 *
 * exifr.gps() は DMS/NSEW を処理した decimal degrees を返す。HEIC の EXIF も読める。
 * GPS なし / 非対応 / 読み取り失敗は location:null (UI 側で手動ピン入力にフォールバック)。
 */

export interface PhotoExif {
  /** EXIF GPS (10進度)。無ければ null。 */
  location: { latitude: number; longitude: number } | null;
  /** 撮影時刻 (DateTimeOriginal / CreateDate) の ms epoch。無ければ null (並び替え/重複判定に使う)。 */
  captureTimeMs: number | null;
}

function isFiniteNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export async function readPhotoExif(file: File): Promise<PhotoExif> {
  let location: { latitude: number; longitude: number } | null = null;
  let captureTimeMs: number | null = null;

  try {
    const gps = await exifr.gps(file);
    if (gps && isFiniteNum(gps.latitude) && isFiniteNum(gps.longitude)) {
      location = { latitude: gps.latitude, longitude: gps.longitude };
    }
  } catch {
    /* GPS 無し / 非対応 → null */
  }

  try {
    const parsed = (await exifr.parse(file, ['DateTimeOriginal', 'CreateDate'])) as
      | { DateTimeOriginal?: unknown; CreateDate?: unknown }
      | undefined;
    const dt = parsed?.DateTimeOriginal ?? parsed?.CreateDate;
    if (dt instanceof Date && !Number.isNaN(dt.getTime())) {
      captureTimeMs = dt.getTime();
    }
  } catch {
    /* 撮影時刻 無し → null */
  }

  return { location, captureTimeMs };
}
